import 'react-native-url-polyfill/auto';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient } from '@supabase/supabase-js';
import { AppState } from 'react-native';

const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing EXPO_PUBLIC_SUPABASE_URL / EXPO_PUBLIC_SUPABASE_ANON_KEY — create apps/mobile/.env (see .env.example).'
  );
}

// A stalled request (flaky network, a hung TLS/CORS handshake to the
// self-hosted Supabase host) must never hang forever — with no timeout, a
// single stuck fetch (including the auth-refresh call `getSession()` awaits
// on startup) freezes the whole app on its loading spinner indefinitely.
const FETCH_TIMEOUT_MS = 15000;
function timeoutFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  // Always drive the fetch off OUR controller so the timeout can actually
  // abort it. supabase-js sometimes passes its own `init.signal`; the old
  // `signal: init?.signal ?? controller.signal` handed control to that signal
  // and silently disabled the timeout, so a hung request ran unbounded. Bridge
  // the caller's signal into our controller instead of dropping ours.
  if (init?.signal) {
    if (init.signal.aborted) controller.abort();
    else init.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: AsyncStorage,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
  },
  global: {
    fetch: timeoutFetch,
  },
});

// Keep token refresh running only while the app is foregrounded.
AppState.addEventListener('change', (state) => {
  if (state === 'active') {
    supabase.auth.startAutoRefresh();
  } else {
    supabase.auth.stopAutoRefresh();
  }
});
